import assert from 'assert-ts';
import React, {useCallback} from 'react';
import {useTheme} from '../../theme';
import {BaseLayout} from '../layout';
import {DropdownProps, Item} from './types';

export const Dropdown: React.FC<DropdownProps> = ({
  state = 'collapsed',
  items,
  favourites,
  filterOutFavourites: hideFavourites = false,
  renderItemContent,
  renderFavouritesSeparator,
}) => {
  const theme = useTheme();
  const renderItems = useCallback(() => {
    const mergedItems = [];

    if (favourites) {
      favourites.forEach(id => {
        const idx = items.findIndex((item: Item) => item.id === id);
        if (assert.soft(idx >= 0, 'Dropdown: Unknown favourite id', {id})) {
          mergedItems.push(renderItemContent(id, idx));
        }
      });
      if (mergedItems.length > 0 && renderFavouritesSeparator) {
        mergedItems.push(renderFavouritesSeparator());
      }
      if (hideFavourites) {
        // Add items not in favourites
        mergedItems.push(
          ...items
            .map((item, idx) =>
              favourites.find(id => item.id === id) !== undefined
                ? null
                : renderItemContent(item.id, idx),
            )
            .filter(itm => itm !== null),
        );
      } else {
        // Add all items
        mergedItems.push(
          ...items.map((item, idx) => renderItemContent(item.id, idx)),
        );
      }
    } else {
      // Add all items
      mergedItems.push(
        ...items.map((item, idx) => renderItemContent(item.id, idx)),
      );
    }

    // Wrap with <div><BaseLayout> to make hight correct on Safari
    return mergedItems.map((item, idx) =>
      item ? (
        <BaseLayout key={idx} vertical>
          {item}
        </BaseLayout>
      ) : null,
    );
  }, [
    favourites,
    hideFavourites,
    items,
    renderFavouritesSeparator,
    renderItemContent,
  ]);

  return state === 'expanded' ? (
    // We use base layout to be able to set a max height value
    <BaseLayout
      flex
      backgroundColor={theme.colors['background_mono_primary']}
      position="absolute"
      zIndex={1000}
      maxHeight={240}
      left={0}
      top={0}
      right={0}
      vertical
      paddingVertical={theme.spacing['8']}
      borderRadius={theme.radii['8']}
      raised
      scrollable>
      {renderItems()}
    </BaseLayout>
  ) : null;
};
